import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageLoader } from "../../components/LoadingMarcenaria";
import { EmptyStateErro, EmptyStateSemPlanilha } from "../../components/EmptyState";
import RailColapsavel from "../../components/RailColapsavel";
import { useExportSnapshot } from "../../hooks/useExportSnapshot";
import useMediaQuery from "../../hooks/useMediaQuery";
import { useRailColapsado } from "../../hooks/useRailColapsado";
import { useDados } from "../../context/DadosContext";
import { useExportRender } from "../../context/ExportRenderContext";
import { usePrecosVisiveis } from "../../context/PrecosContext";
import { mq } from "../../styles/canonical/breakpoints";
import AmbienteDrawer from "./AmbienteDrawer";
import ContextRail from "./ContextRail";
import HeaderBar from "./HeaderBar";
import {
  STATUS_FILTER_OPTIONS,
  applyFilters,
  averageDiasParados,
  buildAmbientesAgrupados,
  buildBreakdownPorTipo,
  buildLinhas,
  buildNextAction,
  reapplyPrimeiroDoAmbiente,
} from "./helpers";
import { isAmbienteEmAcabamento } from "../../utils/acabamento";
import { F } from "../../utils/gerencialFields";
import {
  ClearLink,
  ContextSticky,
  MainColumn,
  PanelToolbar,
  Secao,
  StatusChip,
  StatusChipsRow,
  ThreeZoneGrid,
  ToolbarMeta,
  Wrapper,
} from "./styles";
import { AmbientesView, ItemsView } from "./Views";

const VIEW_MODE_KEY = "pharuerp:acabamento:view-mode";

function readInitialViewMode() {
  if (typeof window === "undefined") return "itens";
  const stored = window.localStorage.getItem(VIEW_MODE_KEY);
  return stored === "ambientes" ? "ambientes" : "itens";
}

export default function Acabamento() {
  const { dados, erro, initialLoading, recarregarDados } = useDados();
  const { precosVisiveis } = usePrecosVisiveis();
  const { renderAll } = useExportRender();
  const { colapsado: railColapsadoPref, alternar: alternarRail } = useRailColapsado(
    "pharuerp:rail-acabamento-colapsado"
  );
  // Durante a exportação (renderAll) o rail sai inteiro, mesmo recolhido.
  const railColapsado = railColapsadoPref && !renderAll;
  // Celular/tablet: sem rail recolhível; os indicadores viram faixa de tiles.
  // Na exportação o layout desktop continua valendo.
  const compacto = useMediaQuery(mq.tablet) && !renderAll;
  const navigate = useNavigate();
  const pageRef = useRef(null);
  const searchInputRef = useRef(null);

  const [viewMode, setViewMode] = useState(readInitialViewMode);
  const [query, setQuery] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState(() => new Set());
  const [selectedTipos, setSelectedTipos] = useState(() => new Set());
  const [ambienteSelecionado, setAmbienteSelecionado] = useState(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(VIEW_MODE_KEY, viewMode);
  }, [viewMode]);

  const todasLinhas = useMemo(() => buildLinhas(dados), [dados]);

  const linhasFiltradas = useMemo(() => {
    const filtered = applyFilters(todasLinhas, {
      query,
      statuses: selectedStatuses,
      tipos: selectedTipos,
    });
    return reapplyPrimeiroDoAmbiente(filtered);
  }, [todasLinhas, query, selectedStatuses, selectedTipos]);

  const ambientesAgrupados = useMemo(
    () => buildAmbientesAgrupados(linhasFiltradas),
    [linhasFiltradas]
  );

  // Breakdown sempre sobre o universo total (não filtrado) para não
  // "evaporar" tipos quando o usuário já filtrou por um deles.
  const breakdown = useMemo(() => buildBreakdownPorTipo(todasLinhas), [todasLinhas]);

  const nextAction = useMemo(() => buildNextAction(todasLinhas), [todasLinhas]);

  const kpis = useMemo(() => {
    const ambientesEmAcab = dados.filter(isAmbienteEmAcabamento);
    const valorParado = ambientesEmAcab.reduce(
      (total, row) => total + (Number(row[F.VALOR_DO_AMBIENTE]) || 0),
      0
    );
    const ambientesAtrasados = new Set(
      todasLinhas.filter((l) => l.statusVariant === "danger").map((l) => l.ambienteIndex)
    ).size;
    return {
      qtdAmbientes: ambientesEmAcab.length,
      itensTotal: todasLinhas.length,
      ambientesAtrasados,
      diasMedios: averageDiasParados(todasLinhas),
      valorParado,
    };
  }, [dados, todasLinhas]);

  const vencidosTotal = useMemo(
    () => todasLinhas.filter((l) => l.statusVariant === "danger").length,
    [todasLinhas]
  );

  const exportPayload = useMemo(() => {
    return {
      titulo: "Pintura & Lacagem",
      // Gate do agendador: enquanto a planilha carrega (1º render, dados
      // vazios) o snapshot NÃO é publicado — sem isso o Excel/CSV agendado
      // saía só com o Resumo zerado (race do iframe oculto).
      // Gate do agendador. `!initialLoading` sozinho nao distingue "carregou"
      // de "falhou": em todo caminho de erro do DadosContext o estado vira
      // dados=[] + erro + loading=false, entao o snapshot saia "pronto" com
      // ZERO linhas — e o Excel/PDF agendado chegava vazio na pasta, sem aviso.
      // E o cenario mais provavel no iframe, que nao tem gesto para pedir
      // permissao do arquivo da planilha.
      pronto: !initialLoading && !erro && dados.length > 0,
      resumo: [
        { label: "Ambientes em Acabamento", value: kpis.qtdAmbientes, type: "number" },
        { label: "Itens pendentes",          value: kpis.itensTotal,   type: "number" },
        { label: "Ambientes atrasados",      value: kpis.ambientesAtrasados, type: "number" },
        { label: "Valor parado",             value: kpis.valorParado,  type: "currency" },
      ],
      secoes: [
        {
          id: "itens",
          label: "Itens pendentes em Acabamento",
          columns: [
            { key: "cliente",    label: "Cliente",   type: "text" },
            { key: "ambiente",   label: "Ambiente",  type: "text" },
            { key: "numeroLote", label: "Lote",      type: "text" },
            { key: "item",       label: "Item",      type: "text" },
            { key: "descricao",  label: "Descrição", type: "text" },
            { key: "entrada",    label: "Entrou",    type: "date" },
            { key: "diasParado", label: "Parado",    type: "days" },
            { key: "prazo",      label: "Prazo",     type: "date" },
            { key: "status",     label: "Status",    type: "status" },
            { key: "valor",      label: "Valor",     type: "currency" },
          ],
          // row.item é um OBJETO ({id, field, label, ...}) — a tela renderiza
          // item.label, mas o export lia o objeto cru e escrevia
          // "[object Object]" no CSV/XLSX/PDF. Achata para o label escalar.
          rows: linhasFiltradas.map((l) => ({ ...l, item: l.item?.label ?? "" })),
        },
      ],
      visual: {
        ref: pageRef,
        // Em viewMode=itens nao incluir o ContextRail (Dashboard lateral
        // com KPIs) na exportacao. O Dashboard so faz sentido na visao
        // de ambientes (que agrega ambiente por ambiente). Mesma decisao
        // que Terceiros 5a.
        sections: viewMode === "itens"
          ? [
              { id: "itens", label: "Lista de itens", selector: "[data-pdf='itens-acabamento']" },
            ]
          : [
              { id: "resumo", label: "Cards de resumo",   selector: "[data-pdf='resumo-acabamento']" },
              { id: "itens",  label: "Lista de ambientes", selector: "[data-pdf='itens-acabamento']" },
            ],
      },
    };
  }, [kpis, linhasFiltradas, viewMode, initialLoading, erro, dados.length]);

  useExportSnapshot("acabamento", exportPayload, pageRef);

  function toggleStatus(value) {
    setSelectedStatuses((current) => {
      const next = new Set(current);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  }

  function toggleTipo(tipoId) {
    setSelectedTipos((current) => {
      const next = new Set(current);
      if (next.has(tipoId)) next.delete(tipoId);
      else next.add(tipoId);
      return next;
    });
  }

  function clearFilters() {
    setQuery("");
    setSelectedStatuses(new Set());
    setSelectedTipos(new Set());
  }

  function handleNextActionCta(cta) {
    if (!cta) return;
    if (cta.kind === "filter-status") {
      setSelectedStatuses(new Set([cta.value]));
    }
  }

  function handleSelectAmbiente(entry) {
    // ItemsView passa a linha (com .row); AmbientesView passa o agrupado (com .row).
    setAmbienteSelecionado(entry);
  }

  if (initialLoading) return <PageLoader scope="acabamento" />;
  if (erro) return <EmptyStateErro erro={erro} onRetry={recarregarDados} />;
  if (!dados.length) return <EmptyStateSemPlanilha onSelectPlanilha={() => navigate("/inicio")} />;

  const filtersActive =
    query.trim().length > 0 || selectedStatuses.size > 0 || selectedTipos.size > 0;
  const totalLinhas = todasLinhas.length;
  const visiveis = linhasFiltradas.length;

  const emptyMessage = filtersActive
    ? "Nenhum item corresponde aos filtros."
    : "Não há itens pendentes em Acabamento no momento.";

  return (
    <Wrapper ref={pageRef}>
      <HeaderBar
        ambientesCount={kpis.qtdAmbientes}
        itensCount={kpis.itensTotal}
        vencidosCount={vencidosTotal}
        query={query}
        onQueryChange={setQuery}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        searchInputRef={searchInputRef}
        exportPayload={exportPayload}
      />

      <ThreeZoneGrid $colapsado={railColapsado && !compacto}>
        <ContextSticky data-pdf="resumo-acabamento">
          {compacto ? (
            <ContextRail
              compacto
              nextAction={nextAction}
              breakdown={breakdown}
              kpis={kpis}
              precosVisiveis={precosVisiveis}
              selectedTipos={selectedTipos}
              onToggleTipo={toggleTipo}
              onNextActionCta={handleNextActionCta}
            />
          ) : (
            <RailColapsavel colapsado={railColapsado} onAlternar={alternarRail}>
              <ContextRail
                nextAction={nextAction}
                breakdown={breakdown}
                kpis={kpis}
                precosVisiveis={precosVisiveis}
                selectedTipos={selectedTipos}
                onToggleTipo={toggleTipo}
                onNextActionCta={handleNextActionCta}
              />
            </RailColapsavel>
          )}
        </ContextSticky>

        <MainColumn>
          <PanelToolbar>
            <StatusChipsRow role="group" aria-label="Filtros de status">
              {STATUS_FILTER_OPTIONS.map((option) => {
                const active = selectedStatuses.has(option.value);
                return (
                  <StatusChip
                    key={option.value}
                    type="button"
                    $tone={option.tone}
                    $active={active}
                    aria-pressed={active}
                    onClick={() => toggleStatus(option.value)}
                  >
                    {option.value}
                  </StatusChip>
                );
              })}
            </StatusChipsRow>
            <ToolbarMeta>
              {filtersActive ? `${visiveis} de ${totalLinhas} itens` : `${totalLinhas} itens`}
              {filtersActive && (
                <>
                  {" "}
                  <ClearLink type="button" onClick={clearFilters}>
                    limpar
                  </ClearLink>
                </>
              )}
            </ToolbarMeta>
          </PanelToolbar>

          <Secao data-pdf="itens-acabamento" data-export-expand>
            {viewMode === "ambientes" ? (
              <AmbientesView
                ambientes={ambientesAgrupados}
                precosVisiveis={precosVisiveis}
                onSelectAmbiente={handleSelectAmbiente}
                emptyMessage={emptyMessage}
              />
            ) : (
              <ItemsView
                linhas={linhasFiltradas}
                precosVisiveis={precosVisiveis}
                onSelectAmbiente={handleSelectAmbiente}
                emptyMessage={emptyMessage}
              />
            )}
          </Secao>
        </MainColumn>
      </ThreeZoneGrid>

      {ambienteSelecionado && (
        <AmbienteDrawer
          ambiente={ambienteSelecionado}
          precosVisiveis={precosVisiveis}
          onClose={() => setAmbienteSelecionado(null)}
        />
      )}
    </Wrapper>
  );
}
